import { useState } from 'react';
import { Editor } from '@tiptap/react';
import { Sparkles, X } from 'lucide-react';
import { VARIABLES } from './VariablesModal';

interface AiEmailModalProps {
  editor: Editor | null;
  onClose: () => void;
}

const AZURE_ENDPOINT = import.meta.env.VITE_AZURE_ENDPOINT;
const AZURE_DEPLOYMENT = import.meta.env.VITE_AZURE_DEPLOYMENT;
const AZURE_API_VERSION = import.meta.env.VITE_AZURE_API_VERSION;
const AZURE_API_KEY = import.meta.env.VITE_AZURE_API_KEY;

export default function AiEmailModal({ editor, onClose }: AiEmailModalProps) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please describe the email you want to generate.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const url = `${AZURE_ENDPOINT}/openai/deployments/${AZURE_DEPLOYMENT}/chat/completions?api-version=${AZURE_API_VERSION}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': AZURE_API_KEY,
        },
        body: JSON.stringify({
          messages: [
            {
              role: 'system',
              content: `You are an expert B2B sales email copywriter. Generate professional, concise, and personalized cold outreach email bodies. Use merge variables where appropriate to personalize the email. The available merge variables are:\n${VARIABLES.map(v => `- ${v.label}: ${v.value}`).join('\n')}\nReturn only the email body text (no subject line, no "Subject:" prefix). Use plain paragraphs — no markdown headers or bullets unless specifically requested.`,
            },
            {
              role: 'user',
              content: prompt,
            },
          ],
          temperature: 0.7,
          max_completion_tokens: 600,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData?.error?.message ?? `Request failed (${res.status})`);
      }

      const data = await res.json();
      const text: string = data?.choices?.[0]?.message?.content ?? '';

      if (!text.trim()) throw new Error('Empty response from AI.');

      if (editor) {
        editor.chain().focus().setContent(`<p>${text.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br/>')}</p>`).run();
      }

      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ai-modal-overlay">
      <div className="ai-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ai-modal-header">
          <div className="ai-modal-title-row">
            <Sparkles size={16} className="ai-modal-icon" />
            <span className="ai-modal-title">AI Email Writer</span>
          </div>
          <button className="ai-modal-close" onClick={onClose} type="button">
            <X size={16} />
          </button>
        </div>

        <p className="ai-modal-hint">
          Describe the email you want to write and AI will generate a professional template for you.
        </p>

        <textarea
          className="ai-modal-textarea"
          placeholder="e.g. Follow-up after a trade show meeting, mention their company's growth strategy and offer a 15-minute call to discuss our outbound sales tool..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={4}
          disabled={loading}
        />

        {error && <p className="ai-modal-error">{error}</p>}

        <div className="ai-modal-footer">
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm ai-modal-generate-btn"
            type="button"
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
          >
            {loading ? (
              <>
                <span className="ai-modal-spinner" /> Generating…
              </>
            ) : (
              <>
                <Sparkles size={13} /> Generate
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
